import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, Link2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { SeverityBadge, StatusBadge } from "../components/badges";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../context/AuthContext";

export default function BlockerCenter() {
  const { t } = useLang();
  const { user } = useAuth();
  const canAct = ["PM", "LEAD", "ADMIN"].includes(user.role);
  const [data, setData] = useState(null);

  const load = useCallback(() =>
    api.get("/blocker-center").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  if (!data)
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>;

  const s = data.summary;
  const stats = [
    { label: t("activeBlockers"), value: s.active_blockers, color: "text-[#9A3412]" },
    { label: t("oldestBlocker"), value: `${s.oldest_blocker_days}d`, color: "text-[#991B1B]" },
    { label: t("openDependencies"), value: s.open_dependencies, color: "text-[#1E3A8A]" },
    { label: t("overdueFlag"), value: s.overdue_dependencies, color: "text-[#991B1B]" },
    { label: t("unownedDecisions"), value: s.unowned_decisions, color: "text-[#92400E]" },
  ];

  const resolveDep = async (id) => {
    try {
      await api.post(`/dependencies/${id}/resolve`);
      toast.success(t("resolve") + " ✓");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="blocker-center-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("blockerCenter")}</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {stats.map((st) => (
          <div key={st.label} className="apha-card p-3">
            <div className={`text-2xl font-heading font-semibold ${st.color}`}>{st.value}</div>
            <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{st.label}</div>
          </div>
        ))}
      </div>

      <section>
        <h2 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-[#9A3412]" /> {t("activeBlockers")} ({data.blockers.length})
        </h2>
        <div className="space-y-3">
          {data.blockers.map((b) => (
            <div key={b.id} className="apha-card p-4" data-testid={`blocker-item-${b.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium bg-secondary rounded px-1.5 py-0.5">{b.project.key}</span>
                <span className="text-sm font-medium">{b.reporter}</span>
                <span className={`text-xs border rounded px-1.5 py-0.5 font-medium ${
                  b.age_days >= 3 ? "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" : "bg-[#FFEDD5] text-[#9A3412] border-[#FED7AA]"
                }`} data-testid={`blocker-age-${b.id}`}>
                  {t("ageDays")}: {b.age_days}
                </span>
                {b.stale && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" /> {t("lastReported")}: {b.reported_on}
                  </span>
                )}
                {b.warning && (
                  <span className="flex items-center gap-1.5 ml-auto">
                    <Link2 className="h-3 w-3 text-muted-foreground" />
                    <SeverityBadge severity={b.warning.severity} />
                    <StatusBadge status={b.warning.status} />
                    {b.warning.owner_name && (
                      <span className="text-xs text-muted-foreground">{t("owner")}: {b.warning.owner_name}</span>
                    )}
                  </span>
                )}
              </div>
              <div className="text-sm mt-2">{b.details}</div>
              {b.impact && (
                <div className="text-xs text-muted-foreground mt-1">{t("blockerImpact")}: {b.impact}</div>
              )}
            </div>
          ))}
          {data.blockers.length === 0 && (
            <div className="apha-card p-8 text-sm text-muted-foreground">{t("noData")}</div>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-heading font-semibold text-base mb-3">{t("openDependencies")} ({data.dependencies.length})</h2>
        <div className="space-y-2">
          {data.dependencies.map((d) => (
            <div key={d.id} className="apha-card p-3.5" data-testid={`dependency-item-${d.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium bg-secondary rounded px-1.5 py-0.5">{d.project?.key}</span>
                <span className="text-xs border rounded px-1.5 py-0.5">{d.dependency_type}</span>
                {d.overdue && (
                  <span className="text-xs bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA] rounded px-1.5 py-0.5 font-medium">
                    {t("overdueFlag")}
                  </span>
                )}
                {d.unowned && (
                  <span className="text-xs bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] rounded px-1.5 py-0.5 font-medium">
                    {t("unownedFlag")}
                  </span>
                )}
                <span className="ml-auto text-xs text-muted-foreground">{t("ageDays")}: {d.age_days}</span>
              </div>
              <div className="text-sm mt-1.5">{d.description}</div>
              <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                <span>{t("owner")}: {d.owner_name || "—"}</span>
                <span>{t("depNeededBy")}: {d.needed_by || "—"}</span>
                <span>{t("source")}: {d.reported_by_name}</span>
                {canAct && (
                  <Button size="sm" variant="outline" className="h-6 text-[11px] ml-auto"
                    data-testid={`resolve-dep-${d.id}`} onClick={() => resolveDep(d.id)}>
                    {t("resolve")}
                  </Button>
                )}
              </div>
            </div>
          ))}
          {data.dependencies.length === 0 && (
            <div className="apha-card p-8 text-sm text-muted-foreground">{t("noData")}</div>
          )}
        </div>
      </section>
    </div>
  );
}
